/**
 * The tmux view's git panel: a repo's commit graph, kept live by the git
 * server's post-receive hook.
 *
 * Who does what. The hook (deploy/git-hooks/post-receive, as `git`) tells todos
 * which refs a push moved (`GIT_HOOK_PATH`); todos believes nothing in it beyond
 * "look again" — it forgets its copy of that repo's graph and tells the admins'
 * tabs. A tab that is showing the repo asks for the graph again, and todos asks
 * the tmux bridge (as `claude`), which fetches into its checkout and reads the
 * log there, so Claude's own unpushed branches show up beside everyone's.
 * todos cannot read the repos itself: its unit has no /home (ProtectHome).
 */

/** Where the hook POSTs, with `?repo=<name>` and post-receive's stdin as text. */
export const GIT_HOOK_PATH = "/api/git/push";

/** How many commits a graph carries, newest first. */
export const GIT_LOG_LIMIT = 250;
/** A graph depth the panel's "Load more" asked for, as a whole number of GIT_LOG_LIMITs:
 *  no ceiling, since it reads on until the repo's first commit. */
export function graphDepth(raw: unknown): number {
  const n = Math.ceil(Number(raw) / GIT_LOG_LIMIT) * GIT_LOG_LIMIT;
  return Number.isFinite(n) ? Math.max(GIT_LOG_LIMIT, n) : GIT_LOG_LIMIT;
}
/** Room for `git log`'s output at `depth` commits: a line each, subject and refs and all. */
export const graphLogBuffer = (depth: number): number => Math.max(1024 * 1024, depth * 2048);
/** How many ref updates one push may name; post-receive's stdin is unbounded. */
export const GIT_PUSH_MAX_UPDATES = 100;

export interface GitCommit {
  hash: string;
  parents: string[];
  author: string;
  email: string;
  /** Committer time, unix ms: what the log is ordered by. */
  at: number;
  subject: string;
  /** Branch and tag names on it, as git decorates them (`main`, `origin/main`, `tag: v1`). */
  refs: string[];
}

export interface GitGraph {
  repo: string;
  commits: GitCommit[];
  /** "checkout": the claude user's clone, local branches and origin's. "server":
   *  the hosted bare repo read directly, when there is no clone yet. */
  source: "checkout" | "server";
  /** Why the fetch before the read failed, when it did: the graph is then the
   *  last one fetched, still worth showing. */
  fetchError: string | null;
  readAt: number;
  /** The checkout and its Claude Code worktrees, with the branch each has out:
   *  what makes a branch's pill a way to a session. Absent from a bridge older
   *  than this, and empty read from the server, which has no worktrees. */
  worktrees?: GitWorktree[];
  /** The commit the checkout has out — its HEAD, on a branch or detached — which
   *  the panel draws a `HEAD` pill on. Absent from a bridge older than this, in a
   *  repo with no commits, and read from the server, where HEAD is no tree's. */
  head?: string;
  /** The local `worktree-…` branches no tree has out whose tip `STAGING_BRANCH`
   *  (else the checkout's HEAD) lacks: what a removed worktree left unmerged, as
   *  Delete keeps such a branch. A queued Claude waiting on that worktree waits
   *  on this (`waitingOn`). Absent from a bridge older than this, and read from
   *  the server. */
  unmerged?: string[];
  /** The `worktree/…` tags (`WORKTREE_TAG_PREFIX`, without `tag: `) whose
   *  worktree's Claude Code conversations are still on disk, so `claude
   *  -c` in a worktree of that name would find them. Absent from a bridge
   *  older than this, and the panel then does not colour by it. */
  claudeTags?: string[];
  /** Output tokens Claude Code has written in each worktree, by worktree name,
   *  over every conversation on disk for it: live ones and those a
   *  `worktree/…` tag is left of. None is left out. Absent from a bridge older
   *  than this, and read from the server. */
  claudeTokens?: Record<string, number>;
  /** The first prompt of the oldest Claude Code conversation on disk for each
   *  worktree, by name, as `claudeTokens`: what it was started to do, for its
   *  pill's tooltip. Cut at `PROMPT_MAX`. Absent as `claudeTokens` is. */
  claudePrompts?: Record<string, string>;
  /** The last text Claude Code wrote in each worktree, by name, as `claudeTokens`:
   *  the newest over its conversations, and when (ms, 0 unknown), for its pill's
   *  popup. Cut at `SAID_MAX`. Absent as `claudeTokens` is. */
  claudeSaid?: Record<string, { text: string; at: number }>;
  /** The task each `worktree/…` tag's worktree was started for, by tag name
   *  (without `tag: `), where the reader may see it: what the panel colours the
   *  tag by. Added by the server per reader (server/worktreeTasks.ts). */
  tagTasks?: Record<string, string>;
  /** What each branch changes against origin's trunk (`origin/main`, else the local
   *  trunk), by its ref as the log names it (`staging`, `origin/x`): from where the
   *  two part (`git diff --shortstat base...branch`), so the trunk moving on adds
   *  nothing. For the line inside a branch's pill. Absent from a bridge older than
   *  this, and read from the server. */
  branchDiffs?: Record<string, BranchDiff>;
}

/** A branch's changes against the trunk: files touched, lines added and removed. */
export interface BranchDiff {
  files: number;
  added: number;
  removed: number;
}

/** `git diff --shortstat`'s one line (" 3 files changed, 40 insertions(+), 2 deletions(-)",
 *  either count left out when 0; nothing at all for no change). */
export function parseShortstat(out: string): BranchDiff {
  const n = (re: RegExp): number => Number(re.exec(out)?.[1] ?? 0);
  return { files: n(/(\d+) files? changed/), added: n(/(\d+) insertions?\(\+\)/), removed: n(/(\d+) deletions?\(-\)/) };
}

/** The checkout (`name` null) or one of its Claude Code worktrees, and the local branch it has out. */
export interface GitWorktree {
  name: string | null;
  branch: string;
  /** Uncommitted changes in it, untracked files included, as of the graph's
   *  read. Absent from a bridge older than this. */
  dirty?: boolean;
  /** Commits it has that `STAGING_BRANCH` (else the checkout's HEAD) lacks, and the other way
   *  round, as of the graph's read. Absent from the checkout's own entry, from
   *  an older bridge, and when git could not say. */
  ahead?: number;
  behind?: number;
  /** The commits made IN it (its branch's reflog, `ownCommits()`), newest first:
   *  not the ones it was made at or took in by a merge or fast-forward. Absent from
   *  the checkout's entry, an older bridge, or when the reflog could not be read. */
  own?: string[];
  /** The task its Claude button was started for (server/worktreeTasks.ts), when
   *  the reader may see it. Added by the server per reader, never by the bridge. */
  taskId?: string;
  taskTitle?: string;
  /** An auto-fix worktree's branch it goes back into, where that is not
   *  staging (`fix_targets`). Added by the server, never by the bridge. */
  fixInto?: string;
}

/** How far main may move past a worktree it has all of before the worktree is
 *  removed on its own (server/worktreePrune.ts). */
export const WORKTREE_STALE_COMMITS = 20;

/**
 * The Claude Code worktrees done with: main has every commit of each and has
 * moved `WORKTREE_STALE_COMMITS` or more past it, and it holds no uncommitted
 * work. Only what the graph says — whether anyone is still in one is the
 * sessions', and the caller's to ask.
 */
export function staleWorktrees(worktrees: readonly GitWorktree[]): (GitWorktree & { name: string })[] {
  return worktrees.filter(
    (w): w is GitWorktree & { name: string } =>
      w.name !== null && w.ahead === 0 && (w.behind ?? 0) >= WORKTREE_STALE_COMMITS && w.dirty === false,
  );
}

export interface GitRefUpdate {
  ref: string;
  /** All zeros when the push created the ref. */
  old: string;
  /** All zeros when the push deleted it. */
  new: string;
}

/** One push, as the hook reported it. Broadcast to admins, never persisted. */
export interface GitPush {
  repo: string;
  at: number;
  updates: GitRefUpdate[];
}

/** Unit and record separators: a subject can hold anything printable. */
const US = "\x1f";
const RS = "\x1e";
export const GIT_LOG_FORMAT = ["%H", "%P", "%an", "%ae", "%ct", "%s", "%D"].join("%x1f") + "%x1e";

/** What `git log --format=GIT_LOG_FORMAT` printed. */
export function parseGitLog(out: string): GitCommit[] {
  const commits: GitCommit[] = [];
  for (const record of out.split(RS)) {
    const [hash = "", parents = "", author = "", email = "", ct = "", subject = "", decor = ""] = record.replace(/^\n/, "").split(US);
    if (!/^[0-9a-f]{40,64}$/.test(hash)) continue;
    commits.push({
      hash,
      parents: parents.split(" ").filter(Boolean),
      author,
      email,
      at: (Number(ct) || 0) * 1000,
      subject,
      refs: decorations(decor),
    });
  }
  return commits;
}

/** `HEAD -> main, origin/main, origin/HEAD, tag: v1` → `main, origin/main, tag: v1`.
 *  HEAD is left to `GitGraph.head`: the bare repo's says nothing to anyone. */
function decorations(decor: string): string[] {
  return decor
    .split(", ")
    .map((d) => d.trim().replace(/^HEAD -> /, ""))
    .filter((d) => d && d !== "HEAD" && !/(^|\/)HEAD$/.test(d));
}

const ZERO = /^0+$/;
const REF_LINE = /^([0-9a-f]{40,64}) ([0-9a-f]{40,64}) (refs\/\S+)$/;

/** post-receive's stdin — `<old> <new> <ref>` a line — with anything else dropped. */
export function parsePushLines(text: string): GitRefUpdate[] {
  const updates: GitRefUpdate[] = [];
  for (const line of text.split("\n")) {
    const m = REF_LINE.exec(line.trim());
    if (m) updates.push({ old: m[1]!, new: m[2]!, ref: m[3]! });
    if (updates.length >= GIT_PUSH_MAX_UPDATES) break;
  }
  return updates;
}

/** What a ref update did, in the words the panel's feed uses. */
export function describeUpdate(u: GitRefUpdate): { name: string; kind: "created" | "deleted" | "updated" } {
  const name = u.ref.replace(/^refs\/(heads|tags)\//, (_, k: string) => (k === "tags" ? "tag " : ""));
  return { name, kind: ZERO.test(u.old) ? "created" : ZERO.test(u.new) ? "deleted" : "updated" };
}

/** What a repo's trunk may be called, in the order one is looked for: a repo
 *  with both is taken to have moved to `main`. */
export const TRUNKS = ["main", "master"] as const;

/**
 * The repo's trunk — `main`, or `master` on a repo that has that and no main —
 * read off the branches in the graph, the checkout's or the git server's.
 * `main` when neither is there, which every caller then reports as missing.
 * Everything below that says "main" means this.
 */
export function trunkOf(commits: readonly GitCommit[]): string {
  for (const b of TRUNKS) if (commits.some((c) => c.refs.includes(b) || c.refs.includes(`origin/${b}`))) return b;
  return TRUNKS[0];
}

/** The branches that count as main: the checkout's own, which "Merge into
 *  main" moves before anything is pushed, and the git server's. */
export const mainRefs = (trunk: string): string[] => [trunk, `origin/${trunk}`];

/**
 * The commits main already has: everything reachable from `main` or
 * `origin/main` (`trunkOf`) within the graph, so the panel can tell a branch that is
 * merged from one that is not. Null when the graph has no main to ask.
 *
 * Only as far as the graph goes. It is the newest GIT_LOG_LIMIT commits by
 * date, and main's path back to a branch's tip runs through commits newer
 * than that tip, so a tip in the graph that main reaches is reached within it.
 */
export function mainHistory(commits: readonly GitCommit[]): Set<string> | null {
  return historyOf(commits, mainRefs(trunkOf(commits)));
}

/**
 * The commits reachable from any of `refs` within the graph (a tag as git
 * decorates it, `tag: <name>`). Null when none of them is in the graph. The
 * panel asks it of the `deployed` tag, to tell what main has from what is live.
 */
export function historyOf(commits: readonly GitCommit[], refs: readonly string[]): Set<string> | null {
  const byHash = new Map(commits.map((c) => [c.hash, c]));
  const queue = commits.filter((c) => c.refs.some((r) => refs.includes(r))).map((c) => c.hash);
  if (!queue.length) return null;
  const seen = new Set<string>();
  while (queue.length) {
    const h = queue.pop()!;
    if (seen.has(h)) continue;
    const c = byHash.get(h);
    if (!c) continue;
    seen.add(h);
    queue.push(...c.parents);
  }
  return seen;
}

/**
 * One row of the drawn graph. Lanes are columns; a lane holds the commit it is
 * waiting for, and keeps its column until that commit is reached, so a line
 * that passes a row is always straight.
 */
export interface GraphRow {
  /** The column this commit's dot is in. */
  col: number;
  /** Lanes that go straight through the row, top to bottom. */
  passing: number[];
  /** Lanes that end at this commit: a line from their column at the top to the dot. */
  incoming: number[];
  /** Lanes the parents are in: a line from the dot to their column at the bottom. */
  outgoing: number[];
  /** Lanes that bend into this commit's column below it, without touching the
   *  dot: a lane to the right was waiting for this commit's first parent, and
   *  history moves left to meet it, as `git log --graph`'s `|/` does. */
  joining: number[];
}

/**
 * The log cut down to the commits `keep` takes, each one's parents rewritten to
 * the nearest kept commits below it, as `git log --simplify-by-decoration` does:
 * the lanes of what is left then join where the history does. Only lines that
 * lead somewhere are kept: none to a parent outside the list, and none to a kept
 * commit another of its parents already reaches.
 */
export function keepCommits(commits: GitCommit[], keep: (c: GitCommit) => boolean): GitCommit[] {
  /** Hash → the kept commits it stands for; unlisted hashes stand for none. */
  const reps = new Map<string, string[]>();
  /** Kept hash → every kept commit below it. */
  const below = new Map<string, Set<string>>();
  const kept: GitCommit[] = [];
  // Parents come after their children, so walking up from the bottom has every
  // parent's answer ready before the child asks.
  for (let i = commits.length - 1; i >= 0; i--) {
    const c = commits[i]!;
    const nearest = [...new Set(c.parents.flatMap((p) => reps.get(p) ?? []))];
    if (!keep(c)) {
      reps.set(c.hash, nearest);
      continue;
    }
    reps.set(c.hash, [c.hash]);
    const parents = nearest.filter((p) => !nearest.some((q) => q !== p && below.get(q)!.has(p)));
    below.set(c.hash, new Set(parents.flatMap((p) => [p, ...below.get(p)!])));
    kept.push({ ...c, parents });
  }
  return kept.reverse();
}

/**
 * Lays commits out in lanes. The commits must list children before parents
 * (`git log --date-order` or `--topo-order`); a parent not in the list keeps
 * its lane running to the bottom, which is what a truncated log should look like.
 *
 * Columns are never compacted, only reused once free: compacting would bend
 * every line to the right of a lane that ends.
 */
export function layoutGraph(commits: GitCommit[]): { rows: GraphRow[]; width: number } {
  const lanes: (string | null)[] = [];
  const rows: GraphRow[] = [];
  let width = 0;
  const free = (): number => {
    const i = lanes.indexOf(null);
    if (i >= 0) return i;
    lanes.push(null);
    return lanes.length - 1;
  };

  for (const c of commits) {
    const before = lanes.slice();
    const incoming: number[] = [];
    before.forEach((h, i) => h === c.hash && incoming.push(i));
    // A branch tip nothing above leads to starts a lane of its own.
    const col = incoming.length ? incoming[0]! : free();
    for (const i of incoming) lanes[i] = null;
    const passing: number[] = [];
    before.forEach((h, i) => h !== null && h !== c.hash && passing.push(i));

    const outgoing: number[] = [];
    const joining: number[] = [];
    c.parents.forEach((p, k) => {
      // A parent another lane is already waiting for: join it rather than
      // draw the same history twice — pulling it left into this column when
      // it is the first parent, so the mainline stays the leftmost line.
      const waiting = lanes.indexOf(p);
      if (waiting > col && k === 0 && lanes[col] === null) {
        lanes[waiting] = null;
        lanes[col] = p;
        joining.push(waiting);
        passing.splice(passing.indexOf(waiting), 1);
        outgoing.push(col);
      } else if (waiting >= 0) outgoing.push(waiting);
      else {
        const lane = k === 0 && lanes[col] === null ? col : free();
        lanes[lane] = p;
        outgoing.push(lane);
      }
    });
    while (lanes.length && lanes[lanes.length - 1] === null) lanes.pop();
    width = Math.max(width, before.length, col + 1, lanes.length);
    rows.push({ col, passing, incoming, outgoing, joining });
  }
  return { rows, width };
}

/**
 * Gathers each dependency tree in `order` (rows joined by `deps`, either way
 * round, however indirectly) into one run, so its lines are short and it reads
 * as one piece, moving as little as it can. The list reads bottom up (what
 * starts next at the bottom), so a tree gathers at its BOTTOM-most row, which
 * stays where it was, and is laid out depth first above it: each row's
 * dependants go straight above it, each one's own branch as one unbroken run
 * before the next's begins (the one sorted nearer the bottom nearest), so two
 * branches never interleave and their lines never cross. A row waiting on
 * several goes above the last of them to be placed. A row in no tree does not
 * move. `deps` is `layoutDeps()`'s; one not in `order` is left out.
 */
export function groupDeps(order: readonly string[], deps: ReadonlyMap<string, readonly string[]>): string[] {
  const pos = new Map(order.map((id, i) => [id, i]));
  const on = (id: string): string[] => [...new Set((deps.get(id) ?? []).filter((d) => d !== id && pos.has(d)))];
  const parent = new Map(order.map((id) => [id, id]));
  const find = (id: string): string => {
    let r = id;
    while (parent.get(r) !== r) r = parent.get(r)!;
    parent.set(id, r);
    return r;
  };
  const dependants = new Map<string, string[]>();
  for (const id of order)
    for (const d of on(id)) {
      parent.set(find(d), find(id));
      (dependants.get(d) ?? dependants.set(d, []).get(d)!).push(id);
    }
  for (const list of dependants.values()) list.sort((a, b) => pos.get(b)! - pos.get(a)!);
  const members = new Map<string, string[]>();
  for (let i = order.length - 1; i >= 0; i--) {
    const root = find(order[i]!);
    (members.get(root) ?? members.set(root, []).get(root)!).push(order[i]!);
  }

  const up: string[] = []; // bottom up
  const done = new Set<string>();
  const ready = (id: string): boolean => on(id).every((d) => done.has(d));
  const place = (id: string): void => {
    done.add(id);
    up.push(id);
    for (const d of dependants.get(id) ?? []) if (!done.has(d) && ready(d)) place(d);
  };
  for (let i = order.length - 1; i >= 0; i--) {
    const tree = members.get(find(order[i]!))!;
    if (done.has(tree[0]!)) continue;
    for (const id of tree) if (!done.has(id) && ready(id)) place(id);
    for (const id of tree) if (!done.has(id)) place(id); // a loop: the queue refuses them, but place it anyway
  }
  return up.reverse();
}

/**
 * Lays out a dependency graph over rows already in order (the queue's), in the
 * log's own `GraphRow`s so it draws the same way. A row's dot sits as far right
 * as the chain it waits on is long — what nothing waits on at the left, its
 * dependants to the right of it — and each row something waits on gets a trunk:
 * a lane running from it past every row in between to the furthest of its
 * dependants, each of which bends into it, as a branch leaves its parent's lane.
 * The trunk keeps the dependency's own column when it can; it moves right past
 * any other row's dot or trunk in its way, so lines never merge or cross a dot.
 * `deps` names, per row, the rows it waits on; one not in `order` is left out.
 * A row in `dotless` has no dot (a heading between the rows, say): lines pass it
 * by without moving aside, and its own row's `col` means nothing. A row in
 * `fixed` sits in the column given there, whatever it waits on (the git panel's
 * worktree feet, in their branch's column in the log), its dependants right of it
 * unless fixed too (the rows on the lead branch's line), and its trunk keeps that column.
 */
export function layoutDeps(
  order: readonly string[],
  deps: ReadonlyMap<string, readonly string[]>,
  dotless: ReadonlySet<string> = new Set(),
  fixed: ReadonlyMap<string, number> = new Map(),
): { rows: GraphRow[]; width: number } {
  const at = new Map(order.map((id, i) => [id, i]));
  const on = (id: string): string[] => [...new Set((deps.get(id) ?? []).filter((d) => d !== id && at.has(d)))];
  const cols = new Map<string, number>();
  const colOf = (id: string, seen: Set<string>): number => {
    const known = cols.get(id);
    if (known !== undefined) return known;
    const pinned = fixed.get(id);
    if (pinned !== undefined) {
      cols.set(id, pinned);
      return pinned;
    }
    if (seen.has(id)) return 0; // a loop: the queue refuses them, but never recurse forever
    seen.add(id);
    const col = Math.max(-1, ...on(id).map((d) => colOf(d, seen))) + 1;
    seen.delete(id);
    cols.set(id, col);
    return col;
  };
  for (const id of order) colOf(id, new Set());

  // One trunk per row something waits on, spanning it and all its dependants.
  const members = new Map<string, Set<string>>();
  for (const id of order) for (const d of on(id)) (members.get(d) ?? members.set(d, new Set([d])).get(d)!).add(id);
  const trunks = [...members].map(([dep, m]) => {
    const rows = [...m].map((id) => at.get(id)!);
    return { dep, m, top: Math.min(...rows), bottom: Math.max(...rows), lane: 0 };
  });
  // A fixed row's trunk is placed first: it keeps the column it was given, and a trunk
  // leaving a row on it moves right instead.
  trunks.sort((a, b) => +!fixed.has(a.dep) - +!fixed.has(b.dep) || a.top - b.top || a.bottom - b.bottom);
  const placed: typeof trunks = [];
  for (const t of trunks) {
    let lane = cols.get(t.dep)!;
    const taken = (l: number): boolean =>
      placed.some((o) => o.lane === l && o.top <= t.bottom && t.top <= o.bottom) ||
      order.some((id, i) => i >= t.top && i <= t.bottom && !t.m.has(id) && !dotless.has(id) && cols.get(id) === l);
    while (taken(lane)) lane++;
    t.lane = lane;
    placed.push(t);
  }

  let width = 1;
  const rows = order.map((id, i): GraphRow => {
    const col = cols.get(id)!;
    const passing = new Set<number>();
    const incoming = new Set<number>();
    const outgoing = new Set<number>();
    for (const t of placed) {
      if (i < t.top || i > t.bottom || t.top === t.bottom) continue;
      if (i > t.top && i < t.bottom) passing.add(t.lane);
      if (!t.m.has(id)) continue;
      // A member bends into the trunk on the side its dependency is; the
      // dependency itself on every side the trunk leaves it by.
      const depAt = at.get(t.dep)!;
      if (i > t.top && (id === t.dep || depAt < i)) incoming.add(t.lane);
      if (i < t.bottom && (id === t.dep || depAt > i)) outgoing.add(t.lane);
    }
    width = Math.max(width, dotless.has(id) ? 1 : col + 1, ...[...passing, ...incoming, ...outgoing].map((l) => l + 1));
    return { col, passing: [...passing], incoming: [...incoming], outgoing: [...outgoing], joining: [] };
  });
  return { rows, width };
}

/**
 * `git worktree list --porcelain`, run in the checkout: the first entry is the
 * checkout itself, and of the rest only Claude Code's
 * (`<checkout>/.claude/worktrees/<name>`) count. A detached one has no branch
 * for a pill to be, so it is left out.
 */
export function parseWorktreeList(out: string): GitWorktree[] {
  const found: GitWorktree[] = [];
  out
    .split(/\n\s*\n/)
    .filter((block) => block.trim())
    .forEach((block, i) => {
      const lines = block.split("\n");
      const path = lines.find((l) => l.startsWith("worktree "))?.slice("worktree ".length) ?? "";
      const branch = lines.find((l) => l.startsWith("branch refs/heads/"))?.slice("branch refs/heads/".length);
      if (!branch) return;
      if (i === 0) found.push({ name: null, branch });
      else {
        const name = /\/\.claude\/worktrees\/([^/]+)$/.exec(path)?.[1];
        if (name) found.push({ name, branch });
      }
    });
  return found;
}

/** The commit the checkout has out, off the same `git worktree list --porcelain`:
 *  its first entry's `HEAD`. Null in a repo with no commits (all zeros). */
export function checkoutHead(out: string): string | null {
  const head = /^HEAD ([0-9a-f]{40,64})$/m.exec(out.split(/\n\s*\n/)[0] ?? "")?.[1];
  return head && !ZERO.test(head) ? head : null;
}

/** The reflog format `ownCommits()` reads: the hash, then git's note of the move. */
export const OWN_COMMITS_FORMAT = "%H %gs";

/**
 * A branch's reflog (`git reflog show --format=OWN_COMMITS_FORMAT`), cut to the
 * commits made on it: `commit:`, and `commit (merge)` / `(amend)`, which are work
 * done there too. Not `branch: Created from …` (where it started) nor `merge …`
 * (what it took in): a worktree made at main is sitting on main's work, not its own.
 */
export function ownCommits(out: string): string[] {
  return out
    .split("\n")
    .map((l) => /^([0-9a-f]{40}) commit(?: \([a-z]+\))?: /.exec(l)?.[1])
    .filter((h): h is string => !!h);
}

/** Whether `ancestor` is `tip` or behind it, as far as the graph goes. */
export function reaches(commits: readonly GitCommit[], tip: string, ancestor: string): boolean {
  const byHash = new Map(commits.map((c) => [c.hash, c]));
  const seen = new Set<string>();
  const queue = [tip];
  while (queue.length) {
    const h = queue.pop()!;
    if (h === ancestor) return true;
    if (seen.has(h)) continue;
    seen.add(h);
    queue.push(...(byHash.get(h)?.parents ?? []));
  }
  return false;
}
